import React, { useState, useImperativeHandle, useRef } from "react"
import { createPortal } from "react-dom"
import { useSnackbar } from "notistack"

// styles
import "@/assets/scss/app/report-problem.scss"

// images
import uploadImage from "@/assets/img/reservations/icons/upload.svg"
import deleteIcon from "@/assets/img/reservations/icons/delete.svg"
import popupHeadImage from "@/assets/img/aside/icons/report2.svg"
import loader from "@/assets/img/loader.svg"

// helpers
import Api from "@/_helpers/Api"

// providers
import { trans } from "@/_providers/Translation"
import { useAuthDataContext } from "@/_providers/Auth"

function ReportProblem(props, ref) {
  const { enqueueSnackbar } = useSnackbar()
  const auth = useAuthDataContext()

  const inputFileRef = useRef(null)

  const getDefaultData = () => {
    const user = auth.user() || {}

    return {
      name: [user.name, user.lastname].filter(Boolean).join(" "),
      email: user.email || "",
      description: "",
      file: null,
      filename: null,
      file_url: null,
    }
  }

  const [popup, setPopup] = useState({
    overlay: false,
    modal: false,
    loading: false,
    data: {},
  })

  const [validations, setValidations] = useState(null)

  useImperativeHandle(ref, () => ({
    open: () => {
      setPopup((prev) => ({
        ...prev,
        loading: false,
        data: getDefaultData(),
      }))

      handleShow()
    },
    hide: () => {
      handleHide()
    },
  }))

  const handleShow = () => {
    setPopup((prev) => ({
      ...prev,
      overlay: true,
    }))

    setTimeout(() => {
      setPopup((prev) => ({
        ...prev,
        overlay: true,
        modal: true,
      }))
    }, 50)
  }

  const handleHide = (e) => {
    if (e) {
      e.preventDefault()
    }

    if (popup.data.file_url) {
      URL.revokeObjectURL(popup.data.file_url)
    }

    setPopup((prev) => ({
      ...prev,
      modal: false,
      loading: false,
      data: {},
    }))

    setValidations(null)

    setTimeout(() => {
      setPopup((prev) => ({
        ...prev,
        overlay: false,
        modal: false,
      }))
    }, 50)
  }

  const handleSave = (e) => {
    e.preventDefault()

    setPopup((prev) => ({
      ...prev,
      loading: true,
    }))

    setValidations(null)

    const data = new FormData(e.target)

    if (popup.data.file) {
      data.append("file", popup.data.file)
    }

    // extra context for whoever handles the report
    data.append("url", window.location.href)
    data.append("user_agent", navigator.userAgent)

    Api.post("bug-report", data)
      .then(() => {
        handleHide()

        enqueueSnackbar(trans("messages.bugReportSent"), {
          variant: "success",
        })
      })
      .catch((error) => {
        enqueueSnackbar(trans("messages.bugReportError"), {
          variant: "error",
        })

        setPopup((prev) => ({
          ...prev,
          loading: false,
        }))

        const _err = error.response

        if (_err && _err.status && _err.status === 422) {
          if (_err.data && _err.data.errors) {
            for (const input of Object.keys(_err.data.errors)) {
              setValidations((prevState) => {
                return { ...prevState, [input]: true }
              })
            }
          }
        }
      })
  }

  const handleInputChange = (e) => {
    const name = e.target.name
    const val = e.target.value

    setPopup((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [name]: val,
      },
    }))
  }

  const handleShowAttach = () => {
    inputFileRef.current?.click()
  }

  const handleAttach = (e) => {
    const file = e.target.files[0]

    inputFileRef.current.value = ""

    if (!file) {
      return
    }

    // accept="image/*" is only a hint for the file picker
    if (!file.type.startsWith("image/")) {
      enqueueSnackbar(trans("labels.allowedImageFormats"), {
        variant: "error",
      })

      return
    }

    if (popup.data.file_url) {
      URL.revokeObjectURL(popup.data.file_url)
    }

    const url = URL.createObjectURL(file)

    setPopup((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        file: file,
        filename: file.name,
        file_url: url,
      },
    }))
  }

  const handleDetach = () => {
    if (popup.data.file_url) {
      URL.revokeObjectURL(popup.data.file_url)
    }

    setPopup((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        file: null,
        filename: null,
        file_url: null,
      },
    }))
  }

  if (!popup.overlay) {
    return null
  }

  return createPortal(
    <div className={`${popup.overlay ? "visible" : ""} overlay`}>
      <form
        className={`${popup.modal ? "show" : ""} popup-primary medium`}
        autoComplete="off"
        onSubmit={handleSave}
      >
        <div className="head-new">
          <img src={popupHeadImage} alt="popup head" />
          <div className="texts">
            <h2>{trans("headings.reportProblem")}</h2>
            <p>{trans("subheadings.reportProblem")}</p>
          </div>
          <span className="close" onClick={handleHide}></span>
        </div>

        <div className="row">
          <div className="col">
            <label htmlFor="bug-report-name">{trans("labels.name")}</label>
            <input
              name="name"
              type="text"
              id="bug-report-name"
              className={validations && validations.name ? "validate" : ""}
              value={popup.data.name || ""}
              onChange={handleInputChange}
            />
          </div>
          <div className="col">
            <label htmlFor="bug-report-email">{trans("labels.email")}</label>
            <input
              name="email"
              type="text"
              id="bug-report-email"
              className={validations && validations.email ? "validate" : ""}
              value={popup.data.email || ""}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col">
            <label htmlFor="bug-report-description">
              {trans("labels.description")}
            </label>
            <textarea
              id="bug-report-description"
              name="description"
              className={
                validations && validations.description ? "validate" : ""
              }
              value={popup.data.description || ""}
              onChange={handleInputChange}
            ></textarea>
          </div>
        </div>

        <div className="row">
          <div className="report-problem-upload">
            {popup.data.file_url && (
              <div className="images-grid">
                <div className="image-item">
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={handleDetach}
                  >
                    <img src={deleteIcon} alt="Delete" />
                  </button>
                  <div className="image-preview">
                    <img src={popup.data.file_url} alt={popup.data.filename} />
                  </div>
                  <div className="image-name">{popup.data.filename}</div>
                </div>
              </div>
            )}

            <input
              ref={inputFileRef}
              type="file"
              accept="image/*,.png,.jpg,.jpeg"
              style={{ display: "none" }}
              onChange={handleAttach}
            />

            <div
              className={`upload-btn ${validations && validations.file ? "invalid" : ""}`}
              onClick={handleShowAttach}
            >
              <img src={uploadImage} alt="Upload" />
              <div>
                <div className="upload-title">
                  {popup.data.file
                    ? trans("buttons.attachOtherPhoto")
                    : trans("buttons.attachPhoto")}
                </div>
                <div className="upload-subtitle">
                  {trans("labels.allowedImageFormats")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="footer align-right">
          {popup.loading ? (
            <img src={loader} alt="loading" />
          ) : (
            <>
              <button className="button" type="button" onClick={handleHide}>
                {trans("buttons.cancel")}
              </button>
              <button className="button button-primary" type="submit">
                {trans("buttons.send")}
              </button>
            </>
          )}
        </div>
      </form>
    </div>,
    document.body,
  )
}

export default React.forwardRef(ReportProblem)
